'use client';

import { ShareIcon } from 'lucide-react';
import { useState } from 'react';
import { useWindowSize } from '../../hooks/useWindowSize';
import { FacebookIcon } from '../icons/FacebookIcon';
import { TelegramIcon } from '../icons/TelegramIcon';
import { XIcon } from '../icons/XIcon';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Drawer, DrawerContent, DrawerFooter } from '../ui/drawer';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const ShareModal: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [showLinkCopiedText, setShowLinkCopiedText] = useState<boolean>(false);

  const { width } = useWindowSize();
  const isMobile = !!width && width < 640;

  const shareLink = window.location.href;
  const shareText = 'Share Chat';
  const title = 'Share Chat';
  const subtitle = 'Anyone who has this link will be able to run this Chat.';

  const social = {
    twitter: `https://twitter.com/intent/tweet?url=${shareLink}&text=${shareText}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareLink}`,
    telegram: `https://telegram.me/share/url?url=${shareLink}&text=${shareText}`,
  };

  const trigger = (
    <Button
      variant="ghost"
      size="icon"
      className="bitte-bg-black bitte-border bitte-border-input"
    >
      <ShareIcon className="bitte-size-4" />
    </Button>
  );

  const handleCopyLink = async () => {
    const url = new URL(shareLink);

    await navigator.clipboard.writeText(url.href);

    setShowLinkCopiedText(true);
    setTimeout(() => setShowLinkCopiedText(false), 3000);
  };

  const dialogTitleInfo = (
    <>
      <DialogTitle className="bitte-mb-2 bitte-text-[20px] bitte-font-semibold">
        {title}
      </DialogTitle>
      {subtitle && <p className="bitte-text-[14px]">{subtitle}</p>}
    </>
  );

  if (!isMobile) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent className="sm:w-[400px] bitte-border-input">
          <DialogHeader>
            <DialogTitle className="bitte-mb-2 bitte-text-center bitte-text-xl bitte-text-white">
              {title}
            </DialogTitle>
            <DialogDescription className="bitte-text-center bitte-text-white">
              {subtitle}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="smart-action-link" className="bitte-text-white">
              Link
            </Label>
            <Input
              id="smart-action-link"
              value={shareLink}
              readOnly
              className="bitte-text-white"
            />
          </div>
          <div className="bitte-flex bitte-items-center bitte-gap-4">
            <Button
              className="bitte-w-full"
              variant="outline"
              onClick={() => window.open(social.twitter, '_blank')}
            >
              <XIcon />
            </Button>
            <Button
              className="bitte-w-full"
              variant="outline"
              onClick={() => window.open(social.telegram, '_blank')}
            >
              <TelegramIcon />
            </Button>
            <Button
              className="bitte-w-full"
              variant="outline"
              onClick={() => window.open(social.facebook, '_blank')}
            >
              <FacebookIcon />
            </Button>
          </div>
          <div>
            <Button className="bitte-w-full" onClick={handleCopyLink}>
              {showLinkCopiedText ? 'Copied' : 'Copy Link'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DrawerContent className="bitte-flex bitte-gap-4 bitte-px-2 bitte-border-input">
        <div className="bitte-text-center bitte-text-white">
          {dialogTitleInfo}
        </div>
        <div className="bitte-mt-4 bitte-grid bitte-w-full bitte-items-center bitte-gap-1.5">
          <Label htmlFor="smart-action-link" className="bitte-text-white">
            Link
          </Label>
          <Input
            id="smart-action-link"
            value={shareLink}
            readOnly
            className="bitte-text-white"
          />
        </div>
        <div className="bitte-flex bitte-items-center bitte-gap-4">
          <Button
            className="bitte-w-full"
            variant="outline"
            onClick={() => window.open(social.twitter, '_blank')}
          >
            <XIcon />
          </Button>
          <Button
            className="bitte-w-full"
            variant="outline"
            onClick={() => window.open(social.telegram, '_blank')}
          >
            <TelegramIcon />
          </Button>
          <Button
            className="bitte-w-full"
            variant="outline"
            onClick={() => window.open(social.facebook, '_blank')}
          >
            <FacebookIcon />
          </Button>
        </div>
        <DrawerFooter className="bitte-gap-4 bitte-border-t bitte-border-input bitte-p-4">
          <div>
            <Button className="bitte-w-full" onClick={handleCopyLink}>
              {showLinkCopiedText ? 'Copied' : 'Copy Link'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default ShareModal;
